import { CommonModule } from '@angular/common'
import { Component, computed, inject, input, linkedSignal } from '@angular/core'

import { Store } from '@ngrx/store'
import { produce } from 'immer'

import type {
  ActionCategory,
  ActionsDiff,
  SelectedActions,
} from '../../conversation/ai-conversation.dictionary'
import { ExecutionPlanActions } from '../../conversation/execution-plan.actions'
import { AiButtonComponent } from '../../modules/external/components/ai-action-button/ai-action-button.component'

@Component({
  selector: 'flx-execution-plan-editor',
  templateUrl: './execution-plan-editor.component.html',
  imports: [CommonModule, AiButtonComponent],
})
export class ExecutionPlanEditorComponent {
  #store = inject(Store)

  actionsDiff = input.required<Omit<ActionsDiff, 'displayTitle'>>()

  categoryLabels: Record<ActionCategory, string> = {
    'process': 'Process',
    'ui': 'UI',
    'business-logic': 'Business Logic',
    'expression': 'Expression',
  }

  displayedActions = computed(() =>
    produce(this.actionsDiff(), (draft) => {
      draft.added = draft.added.map((action) => ({
        ...action,
        displayTitle: `[${this.categoryLabels[action.category]}] ${action.title}`,
      }))
      draft.updated = draft.updated.map((action) => ({
        ...action,
        displayTitle: `[${this.categoryLabels[action.category]}] ${action.title}`,
      }))
      draft.deleted = draft.deleted.map((action) => ({
        ...action,
        displayTitle: `[${this.categoryLabels[action.category]}] ${action.title}`,
      }))
    })
  )

  selectedActions = linkedSignal<SelectedActions>(() => {
    const actions = this.displayedActions()
    return {
      added: new Set(actions.added.map((a) => a.id)),
      updated: new Set(actions.updated.map((a) => a.id)),
      deleted: new Set(actions.deleted.map((a) => a.id)),
    }
  })

  toggleAction(group: keyof ActionsDiff, actionId: string): void {
    this.selectedActions.update((current) =>
      produce(current, (draft) => {
        if (draft[group].has(actionId)) {
          draft[group].delete(actionId)
        } else {
          draft[group].add(actionId)
        }
      })
    )
  }

  isSelected(group: keyof ActionsDiff, actionId: string): boolean {
    return this.selectedActions()[group].has(actionId)
  }

  keepExecutionPlanUpdates(): void {
    const selected = this.selectedActions()
    this.#store.dispatch(
      ExecutionPlanActions.keepExecutionPlanUpdates({
        plan: {
          added: Array.from(selected.added),
          updated: Array.from(selected.updated),
          deleted: Array.from(selected.deleted),
        },
      })
    )
  }

  discardExecutionPlanUpdate(): void {
    this.#store.dispatch(ExecutionPlanActions.discardExecutionPlanUpdate())
  }
}
